import React, { createContext, useContext, useState, useCallback } from "react";
import { AlertTriangle, Trash2, X } from "lucide-react";

const ConfirmContext = createContext(null);

/**
 * useConfirm() — replaces window.confirm with a styled async dialog.
 * Usage:
 *   const confirm = useConfirm();
 *   if (!await confirm({ title, message, confirmLabel, variant })) return;
 */
export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm must be used inside <ConfirmProvider>");
  return ctx;
}

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null); // {title, message, confirmLabel, cancelLabel, variant, resolve}

  const confirm = useCallback((opts = {}) => {
    return new Promise((resolve) => {
      setState({
        title: opts.title || "Bestätigen",
        message: opts.message || "",
        confirmLabel: opts.confirmLabel || "Löschen",
        cancelLabel: opts.cancelLabel || "Abbrechen",
        variant: opts.variant || "danger", // "danger" | "warning" | "primary"
        requireText: opts.requireText || null, // require typing this to confirm
        resolve,
      });
    });
  }, []);

  const onCancel = () => { state?.resolve(false); setState(null); };
  const onConfirm = () => { state?.resolve(true); setState(null); };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state && <Dialog {...state} onCancel={onCancel} onConfirm={onConfirm} />}
    </ConfirmContext.Provider>
  );
}

function Dialog({ title, message, confirmLabel, cancelLabel, variant, requireText, onCancel, onConfirm }) {
  const [typed, setTyped] = useState("");
  const variants = {
    danger: { icon: Trash2, iconBg: "bg-destructive/10 text-destructive", btn: "bg-destructive text-destructive-foreground hover:bg-destructive/90" },
    warning: { icon: AlertTriangle, iconBg: "bg-amber-500/10 text-amber-600", btn: "bg-amber-500 text-white hover:bg-amber-600" },
    primary: { icon: AlertTriangle, iconBg: "bg-primary/10 text-primary", btn: "bg-primary text-primary-foreground hover:bg-primary/90" },
  };
  const v = variants[variant] || variants.danger;
  const Icon = v.icon;

  // Normalize requireText check: case-insensitive + replace umlauts so
  // "löschen"/"LOSCHEN"/"LOESCHEN" all work.
  const normalize = (s) => (s || "")
    .trim().toUpperCase()
    .replace(/Ö/g, "O").replace(/Ä/g, "A").replace(/Ü/g, "U").replace(/ẞ/g, "SS").replace(/OE/g, "O").replace(/AE/g, "A").replace(/UE/g, "U");
  const disabled = requireText ? normalize(typed) !== normalize(requireText) : false;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-in"
      onClick={onCancel}
      data-testid="confirm-dialog"
    >
      <div
        className="bg-card border border-border shadow-2xl max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-start gap-3 px-5 pt-5 pb-3">
          <div className={`p-2.5 rounded-md flex-shrink-0 ${v.iconBg}`}>
            <Icon size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-bold tracking-tight">{title}</h3>
            {message && <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">{message}</p>}
          </div>
          <button onClick={onCancel} className="text-muted-foreground hover:text-foreground p-1" data-testid="confirm-close">
            <X size={16} />
          </button>
        </div>

        {requireText && (
          <div className="px-5 pb-3">
            <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground block mb-1.5">
              Tippe <code className="text-destructive font-mono">{requireText}</code> zum Bestätigen
            </label>
            <input
              autoFocus
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              data-testid="confirm-require-text"
              placeholder={requireText}
              className={`w-full px-3 py-2 text-sm bg-background border focus:outline-none font-mono ${
                typed && !disabled ? "border-emerald-500" : "border-input focus:border-destructive"
              }`}
            />
            {typed && disabled && (
              <p className="text-[11px] text-muted-foreground mt-1">Tipp: Gross-/Kleinschreibung und Umlaute sind egal — einfach das Wort eintippen.</p>
            )}
          </div>
        )}

        <div className="flex gap-2 px-5 py-4 bg-muted/20 border-t border-border">
          <button
            onClick={onCancel}
            data-testid="confirm-cancel"
            className="flex-1 px-4 py-2.5 border border-border hover:bg-muted text-sm font-semibold"
          >
            {cancelLabel}
          </button>
          <button
            onClick={onConfirm}
            disabled={disabled}
            data-testid="confirm-accept"
            autoFocus={!requireText}
            className={`flex-1 px-4 py-2.5 text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed transition-colors ${v.btn}`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
